import { Model, Factory, hasMany, belongsTo, trait } from 'miragejs'
import type { ModelDefinition, ModelInstance } from 'miragejs/-types'
import {
  rand,
  randPastDate,
  randSentence,
  randNumber,
  randBoolean,
} from '@ngneat/falso'
import type { SchemaAttributes } from '../../types.js'
import {
  createErrorResponse,
  getExistingEntity,
  pageable,
  sortable,
} from './../utils.js'
import type { MirageServer, MockServer } from './../server.js'
import { ConsultationPatientTypes } from '@/models/consultation/index.js'

export type ConsultationData = SchemaAttributes<'Consultation'>

interface DiagnosisDefinition {
  name: string
  code: string
}

function getPatientDiagnoses(patientId: number): DiagnosisDefinition[] {
  const diagnosisSets: DiagnosisDefinition[][] = [
    [
      { name: 'Acute Gastroenteritis', code: 'K52.9' },
      { name: 'Dehydration', code: 'E86.0' },
      { name: 'Mild Fever', code: 'R50.9' },
    ],
    [
      { name: 'Otitis Externa', code: 'H60.9' },
      { name: 'Bacterial Infection', code: 'A49.9' },
      { name: 'Allergic Dermatitis', code: 'L23.9' },
    ],
    [
      { name: 'Conjunctivitis', code: 'H10.9' },
      { name: 'Upper Respiratory Infection', code: 'J06.9' },
      { name: 'Nasal Discharge', code: 'R09.89' },
    ],
    [
      { name: 'Osteoarthritis', code: 'M19.90' },
      { name: 'Joint Inflammation', code: 'M13.9' },
      { name: 'Muscle Strain', code: 'M62.9' },
    ],
    [
      { name: 'Dental Disease', code: 'K08.9' },
      { name: 'Gingivitis', code: 'K05.1' },
      { name: 'Oral Infection', code: 'K12.2' },
    ],
  ]

  return diagnosisSets[patientId % diagnosisSets.length]
}

function getPatientDischargeInstructionText(
  patientId: number,
  patientName: string,
): string {
  const medications = [
    [
      'Administer prescribed antibiotics twice daily with food',
      'Give anti-nausea medication 30 minutes before meals',
      'Continue probiotics for 7 days',
    ],
    [
      'Apply topical ointment to affected area twice daily',
      'Give pain medication every 8 hours as needed',
      'Administer eye drops 3 times daily',
    ],
    [
      'Give prescribed anti-inflammatory once daily with breakfast',
      'Administer oral medication with a small treat',
      'Apply ear drops twice daily after cleaning',
    ],
    [
      'Continue heartworm prevention medication monthly',
      'Give joint supplement with evening meal',
      'Administer prescribed steroid with food to prevent stomach upset',
    ],
  ]

  const diets = [
    'Feed bland diet (boiled chicken and rice) for the next 3 days. Gradually transition back to regular food over 5 days.',
    'Provide small, frequent meals throughout the day. Avoid fatty or rich foods for at least one week.',
    'Feed a prescription diet as recommended. Ensure fresh water is always available.',
    'Offer soft food only for the next 48 hours. No treats or hard kibble until cleared by the vet.',
  ]

  const warnings = [
    'Monitor for any adverse reactions and contact the clinic immediately if symptoms worsen.',
    'Watch for signs of infection such as redness, swelling, or discharge. Call us if you notice any changes.',
    'Keep an eye on energy levels and appetite. Report any significant changes to our team.',
    'Check the surgical site daily for proper healing. Prevent licking with an e-collar if needed.',
  ]

  const selectedMeds = medications[patientId % medications.length]
  const selectedDiet = diets[(patientId + 1) % diets.length]
  const selectedWarning = warnings[(patientId + 2) % warnings.length]

  return `<p><strong>Discharge Instructions for ${patientName}:</strong></p>
<ul>
<li>${selectedMeds[0]}</li>
<li>${selectedMeds[1]}</li>
<li>${selectedMeds[2]}</li>
</ul>
<p><strong>Diet:</strong> ${selectedDiet}</p>
<p>${selectedWarning}</p>`
}

function getGeneralDischargeInstructionText(): string {
  return `<p><strong>General Post-Visit Care Instructions:</strong></p>
<ul>
<li>Keep all patients calm and rested for the next 48 hours</li>
<li>Avoid strenuous exercise or play</li>
<li>Monitor appetite, water intake, and bathroom habits</li>
<li>Check for signs of lethargy, vomiting, or diarrhea</li>
</ul>
<p><strong>Medication Administration:</strong></p>
<p>All medications should be given as prescribed. Do not skip doses or discontinue medication early, even if your pet appears to be feeling better. If you have difficulty administering medication, please contact our clinic for advice on alternative methods.</p>
<p><strong>Dietary Guidelines:</strong></p>
<p>For the next 3-5 days, feed a bland diet consisting of boiled chicken (no skin or bones) mixed with plain white rice in a 1:2 ratio. Offer small, frequent meals (4-6 times daily) rather than large portions. Gradually transition back to regular food by mixing increasing amounts of their normal diet over 5-7 days.</p>
<p><strong>Hydration:</strong></p>
<p>Ensure fresh, clean water is available at all times. Monitor water intake closely - both excessive drinking and reduced intake can be signs of concern. If your pet is reluctant to drink, try offering ice cubes or low-sodium chicken broth.</p>
<p><strong>Activity Restrictions:</strong></p>
<p>Limit physical activity for the recovery period. Short, leashed walks for bathroom breaks only. No running, jumping, playing with other pets, or climbing stairs unsupervised. Crate rest may be recommended for particularly active pets.</p>
<p><strong>Warning Signs - Contact Us Immediately If You Observe:</strong></p>
<ul>
<li>Persistent vomiting (more than 2 episodes in 24 hours)</li>
<li>Bloody stool or vomit</li>
<li>Refusal to eat or drink for more than 24 hours</li>
<li>Extreme lethargy or unresponsiveness</li>
<li>Difficulty breathing or persistent coughing</li>
<li>Swelling at any injection sites</li>
<li>Signs of pain (whimpering, restlessness, or aggression when touched)</li>
</ul>
<p><strong>Follow-up Appointment:</strong> Please schedule a follow-up visit in 7-10 days to assess recovery progress. Call our office to book an appointment.</p>
<p><strong>Emergency Contact:</strong> If you notice any concerning symptoms outside of business hours, please contact our emergency line at 555-0123. For life-threatening emergencies, proceed directly to the nearest 24-hour veterinary emergency clinic.</p>`
}

export const model = (Model as ModelDefinition<ConsultationData>).extend({
  department: belongsTo('department'),
  patients: hasMany('patient'),
  appointments: hasMany('schedulingEvent'),
  client: belongsTo('client'),
  ward: belongsTo('ward'),
  invoice: belongsTo('invoice', {
    inverse: 'consultation',
  }),
  responsibleSurgeons: hasMany('user'),
  responsibleNurses: hasMany('user'),
  consultationDiagnoses: hasMany('consultationDiagnosis'),
  consultationDischargeInstructions: hasMany(
    'consultationDischargeinstruction',
  ),
  consultationConversations: hasMany('consultationConversation'),
  complaintType: belongsTo('complaintType'),
  residentVets: hasMany('user'),
  supervisingVeterinarian: belongsTo('user'),
  reportingDimension1: belongsTo('listItem'),
  reportingDimension2: belongsTo('listItem'),
  patientLocation: belongsTo('patientLocation', {
    inverse: 'patientLocation',
  }),
  patientLocation2: belongsTo('patientLocation', {
    inverse: 'patientLocation',
  }),
})

export const factory = Factory.extend<ConsultationData>({
  status() {
    return rand([
      'ADMITTED',
      'CONSULTATION',
      'WAITING_FOR_DISCHARGE',
      'INVOICING',
      'DISCHARGED',
      'INVOICE_PAID',
      'FINALIZED',
      'DRAFT',
    ])
  },
  created() {
    return randPastDate({ years: 1 }).toISOString()
  },
  started() {
    return randPastDate({ years: 1 }).toISOString()
  },
  admitted_time() {
    const startedDate = new Date(this.started as string)

    // Generate a date between 1 and 90 days before the started date
    const daysBeforeStart = randNumber({ min: 1, max: 90 })
    const admittedDate = new Date(
      startedDate.getTime() - daysBeforeStart * 24 * 60 * 60 * 1000,
    )

    return admittedDate.toISOString()
  },
  patient_type() {
    return rand(Object.values(ConsultationPatientTypes))
  },
  consultation_type() {
    return rand([
      'INPATIENT',
      'OUTPATIENT',
      'HOME_CALL',
      'FARM_VISIT',
      'BOARDING',
      'PET_SERVICE',
    ])
  },
  preliminarynote() {
    return randSentence({ length: randNumber({ min: 1, max: 10 }) })
      .join(' ')
      .slice(0, -1)
  },
  complaint() {
    return randSentence({ length: randNumber({ min: 0, max: 3 }) })
      .join(' ')
      .slice(0, -1)
  },
  withPatientLocation: trait({
    afterCreate(consultation, server) {
      const patientLocation = server.create('patientLocation')
      consultation.update({ patientLocation })
    },
  }),
  withPatientLocation2: trait({
    afterCreate(consultation, server) {
      const patientLocation2 = server.create('patientLocation')
      consultation.update({ patientLocation2 })
    },
  }),
  withRandomPatientHealthPlans: trait({
    afterCreate(consultation, server) {
      const hasRandomHealthPlans = randBoolean()
      if (!consultation.patients.length) {
        consultation.update({
          patients: [
            server.create(
              'patient',
              hasRandomHealthPlans ? 'withHealthPlans' : '',
              {
                archived: false,
              },
            ),
          ],
        })
      } else if (hasRandomHealthPlans) {
        consultation.patients.models.forEach((patient: ModelInstance) => {
          patient.update({
            activeHealthPlans: server.createList(
              'patientHealthPlan',
              randNumber({ min: 1, max: 10 }),
            ),
          })
        })
      }
    },
  }),
  withInvoice: trait({
    afterCreate(consultation, server) {
      let client = consultation.client
      if (!client) {
        client = getExistingEntity(server, 'client')
        consultation.update({ client })
      }

      const invoice = server.create('invoice', 'withRows', {
        client,
        department: consultation.department,
        patients: consultation.patients.models,
      })

      consultation.update({ invoice })
    },
  }),
  withDischargeInstructions: trait({
    afterCreate(consultation, server) {
      const patients = consultation.patients?.models || []

      // Create one discharge instruction per patient
      patients.forEach((patient: ModelInstance) => {
        const patientData = patient as unknown as { id: string; name: string }
        server.create('consultationDischargeinstruction', {
          consultation,
          patient,
          text: getPatientDischargeInstructionText(
            Number(patientData.id),
            patientData.name || 'Patient',
          ),
        })
      })

      // Create one general discharge instruction
      server.create('consultationDischargeinstruction', {
        consultation,
        patient: null,
        text: getGeneralDischargeInstructionText(),
      })
    },
  }),
  withDiagnoses: trait({
    afterCreate(consultation, server) {
      const patients = consultation.patients?.models || []

      patients.forEach((patient: ModelInstance) => {
        const patientId = Number((patient as unknown as { id: string }).id)
        const diagnoses = getPatientDiagnoses(patientId)

        diagnoses.forEach((diagnosis) => {
          server.create('consultationDiagnosis', {
            consultation,
            patient,
            name: diagnosis.name,
            code: diagnosis.code,
            diagnosis_type: 'FINAL',
            category: 'PRIMARY',
          })
        })
      })
    },
  }),
  afterCreate(consultation, server) {
    if (!consultation.client) {
      const client = getExistingEntity(server, 'client')
      consultation.update({ client })
    }

    if (!consultation.ward) {
      const ward = getExistingEntity(server, 'ward')
      consultation.update({ ward })
    }

    if (!consultation.department) {
      consultation.update({
        departmentId: server.schema.first('department').id,
      })
    }

    if (!consultation.complaintType) {
      const complaintType = server.create(
        'complaintType',
        'withComplaintTypeGroup',
      )
      consultation.update({ complaintType })
    }

    if (!consultation.patients.length) {
      consultation.update({
        patients: [
          server.create('patient', 'withBreed', 'withTags', {
            archived: false,
          }),
        ],
      })
    }

    if (!consultation.residentVets.length) {
      const residentVets = [getExistingEntity(server, 'user')]
      consultation.update({ residentVets })
    }

    if (!consultation.responsibleSurgeons.length) {
      const responsibleSurgeons = [getExistingEntity(server, 'user')]
      consultation.update({ responsibleSurgeons })
    }

    if (!consultation.responsibleNurses.length) {
      const existingUsers = server.schema.all('user').models
      let responsibleNurses = []

      if (existingUsers.length >= 2) {
        const firstNurse = rand(existingUsers)
        const remainingUsers = existingUsers.filter(
          (user: ModelInstance) => user !== firstNurse,
        )
        responsibleNurses = [firstNurse, rand(remainingUsers)]
      } else if (existingUsers.length === 1) {
        responsibleNurses = [existingUsers[0], server.create('user')]
      } else {
        responsibleNurses = [server.create('user'), server.create('user')]
      }

      consultation.update({ responsibleNurses })
    }

    if (!consultation.supervisingVeterinarian) {
      const supervisingVeterinarian = getExistingEntity(server, 'user')
      consultation.update({ supervisingVeterinarian })
    }

    if (!consultation.reportingDimension1) {
      const reportingDimension1 = server.create(
        'listItem',
        'asReportingDimension1',
      )
      consultation.update({ reportingDimension1 })
    }

    if (!consultation.reportingDimension2) {
      const reportingDimension2 = server.create(
        'listItem',
        'asReportingDimension2',
      )
      consultation.update({ reportingDimension2 })
    }
  },
})

export function routes(server: MockServer) {
  server.resource('consultation', { path: '/consultations' })

  server.patch('/consultations/:id', function (schema, request) {
    const id = request.params.id
    const consultation = schema.find('consultation', id)

    if (!consultation) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Consultation with id ${id} not found` }],
      })
    }

    const attrs = JSON.parse(request.requestBody)
    const { relationships } = attrs.data

    const relationshipIds = {
      complaintTypeId: relationships?.complaint_type?.data?.id || null,
      supervisingVeterinarianId:
        relationships?.supervising_veterinarian?.data?.id || null,
      reportingDimension1Id:
        relationships?.reporting_dimension_1?.data?.id || null,
      reportingDimension2Id:
        relationships?.reporting_dimension_2?.data?.id || null,
      wardId: relationships?.ward?.data?.id || null,
      responsibleSurgeonIds:
        relationships?.responsible_surgeons?.data?.map(
          (item: { id: string }) => item.id,
        ) || [],
      responsibleNurseIds:
        relationships?.responsible_nurses?.data?.map(
          (item: { id: string }) => item.id,
        ) || [],
      residentVetIds:
        relationships?.resident_vets?.data?.map(
          (item: { id: string }) => item.id,
        ) || [],
    }

    return consultation.update({
      ...attrs.data.attributes,
      ...relationshipIds,
    }) as unknown as Response
  })

  server.get('/consultations', function (this: MirageServer, schema, request) {
    const consultations = schema.all('consultation')

    // Apply sorting if requested
    const sorted = sortable(consultations, request)

    // Apply pagination if requested
    const { data, meta } = pageable(sorted, request)

    // Return serialized response with pagination meta
    return {
      ...this.serialize(data),
      meta: {
        pagination: meta,
      },
    }
  })

  server.get('/consultations/:id', function (schema, request) {
    const id = request.params.id
    const consultation = schema.find('consultation', id)

    if (!consultation) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Consultation with id ${id} not found` }],
      })
    }

    return consultation
  })

  server.get(
    '/consultations/:id/financial_summary',
    function (schema, request) {
      const id = request.params.id
      const consultation = schema.find('consultation', id)

      if (!consultation) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: `Consultation with id ${id} not found` }],
        })
      }

      const total = randNumber({ min: 100, max: 5000, precision: 2 })
      const estimatesMaxTotal = randNumber({
        min: 100,
        max: 5000,
        precision: 2,
      })
      const estimateUsagePercentOfMax = Number(
        ((total / estimatesMaxTotal) * 100).toFixed(2),
      )

      const mockFinancialSummary = {
        data: {
          type: 'ConsultationFinancialSummary' as const,
          id: 'consultation-financial-summary',
          attributes: {
            id: 'consultation-financial-summary',
            total,
            total_price_change: randNumber({
              min: -200,
              max: 200,
              precision: 2,
            }),
            upper_spend_limit_exceeded: randBoolean(),
            upper_spend_limit_almost_exceeded: randBoolean(),
            estimate_usage_percent_of_max: estimateUsagePercentOfMax,
            estimates_max_total: estimatesMaxTotal,
            estimate_max_limit_almost_exceeded: estimateUsagePercentOfMax > 80,
            consultation_days_planned_totals: Array.from({ length: 3 }, () =>
              randNumber({ min: 50, max: 800, precision: 2 }),
            ),
          },
        },
      }

      return mockFinancialSummary
    },
  )

  server.patch(
    '/consultations/:id/mark_in_progress',
    function (schema, request) {
      const id = request.params.id
      const consultation = schema.find('consultation', id)

      if (!consultation) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: `Consultation with id ${id} not found` }],
        })
      }

      consultation.update({
        status: 'CONSULTATION',
      })

      return consultation
    },
  )

  server.patch(
    '/consultations/:id/mark_ready_for_discharge',
    function (schema, request) {
      const id = request.params.id
      const consultation = schema.find('consultation', id)

      if (!consultation) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: `Consultation with id ${id} not found` }],
        })
      }

      consultation.update({
        status: 'WAITING_FOR_DISCHARGE',
      })

      return consultation
    },
  )
}

export function seeds(server: MockServer) {
  const departments = server.schema.all('department').models

  const patient = server.create(
    'patient',
    'withBreed',
    'withTags',
    'withMicrochip',
    'withInsuranceCompany',
    { archived: false },
  )

  server.create('consultation', 'withPatientLocation', 'withInvoice', {
    department: departments[0],
    patients: [patient],
  })

  const patientNotChipped = server.create(
    'patient',
    'withBreed',
    'withTags',
    'withNotChipped',
    { archived: false },
  )

  server.create('consultation', 'withPatientLocation2', 'withInvoice', {
    department: departments[1],
    patients: [patientNotChipped],
  })
}
